import React, { useCallback, useEffect, useRef, useState } from 'react';
import { validate } from '../../../validation/engine';
import ResponseShell from './ResponseShell';
import FormControl from '../../common/FormControl';
import { useAutosave } from './useAutosave';
import { type ProcedureResponseProps, computeResponseReadOnly } from './responseShellTypes';
import {
  responseService,
  transitionService,
  procedureInstanceService,
  questionnaireTemplateService,
  findingService,
  procedureEvidenceService,
  type Finding,
  type ProcedureEvidence,
} from '../../../services/auditService';
import { useAuth } from '../../../contexts/AuthContext';
import ObservationModal, { type ObservationModalRowContext } from './ObservationModal';
import RaiseFindingButton, { ITEM_TYPE_QUESTIONNAIRE_RESPONSE } from './RaiseFindingButton';
import InlineQuestionMeta from './InlineQuestionMeta';
import EvidenceCell from './EvidenceCell';
import RejectNoteCell from './RejectNoteCell';

/**
 * Plan 05b-03b — sample-less Questionnaire response body.
 *
 * Per CONTEXT D-01 + D-03 (Phase 05b), the Questionnaire procedure type does
 * NOT need a population/sample picker — every question in the procedure's
 * questionnaire_template is answered exactly once per procedure_instance.
 *
 * Backend contract (verified against backend/routes/adt_routes.py +
 * adt_schemas.ADTQuestionnaireResponseBase):
 *
 *   - GET  /adt/questionnaire_responses?procedure_instance_id=N -> rows
 *   - POST /adt/questionnaire_responses
 *         body: { entity_id, procedure_instance_id, questionnaire_template_id,
 *                 question_text, answer_text, created_by }
 *   - PUT  /adt/questionnaire_responses/{id}
 *         body: { question_text?, answer_text?, modified_by }
 *
 * IMPORTANT — schema deviation from PLAN.md <interfaces>:
 *   The plan's interface block listed `question_id: int (FK)` as the linking
 *   field. The grep precondition (re-confirmed in Task 1) shows the actual
 *   schema is `question_text` (NOT NULL) + optional `questionnaire_template_id`.
 *   There is no `question_id` FK column — the response stores the question text
 *   inline so the answer remains readable even if the template is later edited.
 *   This component follows the actual schema (Rule 1 — bug deviation).
 */

interface QuestionnaireRow {
  /** ADT_questionnaire_templates.id — null when no template question exists. */
  questionnaire_template_id: number | null;
  /** Stable React key; for template-backed rows = template_id, for fallback = 0. */
  key: string;
  /** Inline question text (matches backend schema; NOT NULL on POST). */
  question_text: string;
  answer_text: string;
  /**
   * Template-authored evidence_type for this question (D-3). Questionnaire
   * templates DO persist evidence_type, so the per-row EvidenceCell passes the
   * real value (NOT degraded to 'any'). Null/absent for non-template fallback rows.
   */
  evidence_type?: string | null;
  /** Existing ADT_questionnaire_responses.id, if loaded from server. */
  response_id?: number;
  /** G2 — checker per-line reject note (non-empty => line rejected). */
  reject_note: string;
  dirty: boolean;
}

const QuestionnaireResponse: React.FC<ProcedureResponseProps> = ({
  instance,
  role,
  performerName,
  approverName,
  onSaved,
  readOnly: readOnlyProp,
}) => {
  const { state } = useAuth();
  const entityId = state.user?.entity_id ?? 0;
  const currentUserId = state.user?.id ?? 0;
  // G3 — an Approved instance is immutable. Derive from workflow_state unless the
  // Read-only unless the maker (performer) is editing an open instance — shared rule.
  const readOnly = computeResponseReadOnly(instance, role, readOnlyProp);

  const [rows, setRows] = useState<QuestionnaireRow[]>([]);
  const [templateMissing, setTemplateMissing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [obsErrors, setObsErrors] = useState<Record<string, string>>({});

  // Plan 05b-05 / STAFF-06 — inline finding raise state.
  const [findings, setFindings] = useState<Finding[]>([]);
  const [modalRow, setModalRow] = useState<{
    context: ObservationModalRowContext;
    itemRecordId: number | null;
  } | null>(null);
  // Phase 113 REQ-8 — per-row evidence, fetched once and grouped.
  const [evidenceByRow, setEvidenceByRow] = useState<Map<string, ProcedureEvidence[]>>(new Map());

  // Load findings on mount and after each new create.
  const reloadFindings = useCallback(async () => {
    try {
      const list = await findingService.listForProcedure(instance.id);
      setFindings(list);
    } catch {
      // Non-fatal — badges just won't show. Body still works.
    }
  }, [instance.id]);

  const reloadEvidence = useCallback(async () => {
    try {
      const list = await procedureEvidenceService.list(instance.id);
      const map = new Map<string, ProcedureEvidence[]>();
      for (const ev of list) {
        if (ev.item_type_id != null && ev.item_record_id != null) {
          const key = `${ev.item_type_id}-${ev.item_record_id}`;
          const bucket = map.get(key) ?? [];
          bucket.push(ev);
          map.set(key, bucket);
        }
      }
      setEvidenceByRow(map);
    } catch {
      // Non-fatal.
    }
  }, [instance.id]);

  useEffect(() => {
    reloadFindings();
    reloadEvidence();
  }, [reloadFindings, reloadEvidence]);

  // Group findings by (item_type_id, item_record_id) for fast per-row lookup.
  const findingsForRow = useCallback(
    (itemRecordId: number | null): Finding[] => {
      if (itemRecordId == null) return [];
      return findings.filter(
        (f) =>
          f.item_type_id === ITEM_TYPE_QUESTIONNAIRE_RESPONSE &&
          f.item_record_id === itemRecordId,
      );
    },
    [findings],
  );

  // Initial load: best-effort template + responses merge.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1. Fetch the procedure instance to discover its procedure_template_id.
        const inst = await procedureInstanceService.get(instance.id);
        const templateId = inst.procedure_template_id ?? null;

        // 2. Fetch the questionnaire template's questions (one row per question).
        let templateQuestions: Array<{
          id: number;
          question_text: string;
          evidence_type?: string | null;
        }> = [];
        if (templateId) {
          try {
            const items = await questionnaireTemplateService.listForProcedure(templateId);
            templateQuestions = items.map((q) => ({
              id: q.id,
              question_text: q.question_text,
              evidence_type: q.evidence_type ?? null,
            }));
          } catch {
            // Template fetch failed — fall through to existing responses.
          }
        }

        // 3. Fetch any existing responses for this instance.
        const responses = await responseService.list('Questionnaire', instance.id);
        const byTemplateId = new Map<number, any>();
        for (const r of responses) {
          if (r.questionnaire_template_id != null) {
            byTemplateId.set(r.questionnaire_template_id, r);
          }
        }

        // 4. Build the merged row list.
        //    - If template questions exist: one row per template question, merged
        //      with any existing answer.
        //    - Else if responses exist (legacy seed): one row per response.
        //    - Else: surface "Awaiting template" and seed a single free-text row
        //      so the performer can still capture something.
        let merged: QuestionnaireRow[] = [];
        if (templateQuestions.length > 0) {
          merged = templateQuestions.map((q) => {
            const existing = byTemplateId.get(q.id);
            return {
              questionnaire_template_id: q.id,
              key: `tpl-${q.id}`,
              question_text: q.question_text,
              evidence_type: q.evidence_type ?? null,
              answer_text: existing?.answer_text ?? '',
              response_id: existing?.id,
              reject_note: existing?.reject_note ?? '',
              dirty: false,
            };
          });
        } else if (responses.length > 0) {
          merged = responses.map((r: any) => ({
            questionnaire_template_id: r.questionnaire_template_id ?? null,
            key: `resp-${r.id}`,
            question_text: r.question_text ?? '(question text missing)',
            answer_text: r.answer_text ?? '',
            response_id: r.id,
            reject_note: r.reject_note ?? '',
            dirty: false,
          }));
        } else {
          if (!cancelled) setTemplateMissing(true);
          merged = [
            {
              questionnaire_template_id: null,
              key: 'fallback-0',
              question_text: 'Free-form response',
              answer_text: '',
              reject_note: '',
              dirty: false,
            },
          ];
        }
        if (!cancelled) setRows(merged);
      } catch (e: any) {
        if (!cancelled) {
          setLoadError(e?.message ?? 'Failed to load questionnaire.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instance.id]);

  // Debounced save — writes every dirty row, then clears the dirty flag.
  // Latest persisted response_id per row key — readable synchronously right
  // after flush() (state updates are async, so a ref is the reliable source for
  // the Raise-Finding handler that needs the id immediately after saving).
  const idsRef = useRef<Record<string, number>>({});

  const save = useCallback(async () => {
    const dirty = rows.filter((r) => r.dirty && !obsErrors[r.key]);
    if (dirty.length === 0) return;

    // Walk a local snapshot so we can attach response_ids to the next setState.
    const updates: Array<{ key: string; response_id?: number }> = [];
    for (const r of dirty) {
      if (r.response_id) {
        await responseService.update('Questionnaire', r.response_id, {
          question_text: r.question_text,
          answer_text: r.answer_text,
          modified_by: currentUserId,
        });
        updates.push({ key: r.key, response_id: r.response_id });
        idsRef.current[r.key] = r.response_id;
      } else {
        const created = await responseService.create('Questionnaire', {
          entity_id: entityId,
          procedure_instance_id: instance.id,
          questionnaire_template_id: r.questionnaire_template_id ?? undefined,
          question_text: r.question_text,
          answer_text: r.answer_text,
          created_by: currentUserId,
        });
        updates.push({ key: r.key, response_id: created?.id });
        if (created?.id) idsRef.current[r.key] = created.id;
      }
    }

    setRows((prev) =>
      prev.map((r) => {
        const upd = updates.find((u) => u.key === r.key);
        if (!upd) return r;
        return { ...r, response_id: upd.response_id ?? r.response_id, dirty: false };
      })
    );
  }, [rows, obsErrors, entityId, currentUserId, instance.id]);

  const { status, markDirty, flush } = useAutosave(save, 1000);

  // Raise/attach a finding for a question row. Persist any unsaved answer first
  // (questionnaire rows have no sample fallback) so the finding can link to the
  // response row; falls back to a procedure-level finding (null record id) when
  // the row has no answer yet. Replaces the old hard `disabled={!response_id}`
  // gate that made the Raise-Finding popup unusable until autosave fired.
  const openFinding = useCallback(
    async (r: { key: string; response_id?: number; answer_text?: string }) => {
      let recordId: number | null = r.response_id ?? idsRef.current[r.key] ?? null;
      if (recordId == null && (r.answer_text ?? '').trim()) {
        await flush();
        recordId = idsRef.current[r.key] ?? null;
      }
      setModalRow({
        context: {
          procedureName: instance.audit_procedure_name,
          sampleLabel: null,
          responseValue: r.answer_text || null,
        },
        itemRecordId: recordId,
      });
    },
    [flush, instance.audit_procedure_name],
  );

  const updateRow = (idx: number, answer_text: string, rowKey: string) => {
    setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], answer_text, dirty: true };
      return next;
    });
    markDirty();
  };

  const handleObsBlur = (rowKey: string, value: string) => {
    if (!value || !value.trim()) {
      setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
      return;
    }
    const result = validate(110, value);
    if (!result.valid) setObsErrors(prev => ({ ...prev, [rowKey]: result.message ?? 'Invalid answer.' }));
    else setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
  };

  // G2 — number of lines a checker has rejected (drives the shell approve-gate).
  const rejectedCount = rows.filter(r => r.reject_note && r.reject_note.trim()).length;

  // Submit gate (2026-06-29) — every question must be answered before submit.
  const unanswered = rows.filter(r => !(r.answer_text ?? '').trim()).length;
  const incompleteReason =
    rows.length > 0 && unanswered > 0
      ? `Answer all ${rows.length} questions before submitting — ${unanswered} still unanswered.`
      : null;

  return (
    <ResponseShell
      instance={instance}
      procedureType="Questionnaire"
      role={role}
      performerName={performerName}
      approverName={approverName}
      autosaveStatus={status}
      rejectedCount={rejectedCount}
      incompleteReason={incompleteReason}
      onSaveDraft={async () => {
        await flush();
      }}
      onSubmit={async () => {
        await flush();
        await transitionService.submit('procedure', instance.id);
        onSaved?.();
      }}
      onApprove={async () => {
        await transitionService.approve('procedure', instance.id);
        onSaved?.();
      }}
      onReject={async (comment) => {
        await transitionService.reject('procedure', instance.id, comment);
        onSaved?.();
      }}
      onReopen={async () => {
        await transitionService.reopen('procedure', instance.id);
        onSaved?.();
      }}
    >
      {loadError && (
        <div className="response-load-error">{loadError}</div>
      )}
      {templateMissing && (
        <div className="response-empty-warning">
          No questionnaire template attached to this procedure. Capture a free-form
          response below or contact your DRC admin to attach a template.
        </div>
      )}
      <div className="response-section">
        <div className="table-list response-qtable">
          <div className="table-list-scroll">
            <table className="table-list-table">
              <colgroup>
                <col className="response-col-sno" />
                <col />
                <col className="response-col-answer-wide" />
              </colgroup>
              <thead>
                <tr>
                  <th className="table-list-th response-th-sno">S.No</th>
                  <th className="table-list-th">Question</th>
                  <th className="table-list-th">Answer</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const rowFindings = findingsForRow(r.response_id ?? null);
                  const evKey = `${ITEM_TYPE_QUESTIONNAIRE_RESPONSE}-${r.response_id ?? 0}`;
                  const rowEvidence = r.response_id ? (evidenceByRow.get(evKey) ?? []) : [];
                  return (
                    <React.Fragment key={r.key}>
                      <tr className="table-list-row">
                        <td className="table-list-cell response-cell-sno">{idx + 1}</td>
                        <td className="table-list-cell response-cell-question">
                          {/* REQ-7: question text LEFT, action cluster RIGHT, same flex row. */}
                          <div className="response-question-row">
                            <span className="response-question-text">{r.question_text}</span>
                            <div className="response-action-cluster response-action-cluster--inline">
                              {/* Questionnaire has no per-question remark (fix_C). */}
                              {/* Questionnaire templates persist evidence_type → typed (NOT 'any'). */}
                              <EvidenceCell
                                scope="row"
                                evidenceType={r.evidence_type ?? undefined}
                                entityId={entityId}
                                createdBy={currentUserId}
                                procedureInstanceId={instance.id}
                                itemTypeId={ITEM_TYPE_QUESTIONNAIRE_RESPONSE}
                                itemRecordId={r.response_id}
                                questionLabel={r.question_text}
                                disabled={readOnly}
                                showCount={false}
                                onChanged={reloadEvidence}
                              />
                              <RejectNoteCell
                                responseId={r.response_id}
                                rejectNote={r.reject_note}
                                procedureType="Questionnaire"
                                role={role}
                                readOnly={readOnly}
                                onSaved={(note) =>
                                  setRows(prev => prev.map((x) => (x.key === r.key ? { ...x, reject_note: note } : x)))
                                }
                              />
                              <RaiseFindingButton
                                count={rowFindings.length}
                                showCount={false}
                                disabled={readOnly}
                                aria-label="Raise finding"
                                onClick={() => { void openFinding(r); }}
                                title="Raise a finding from this row"
                              />
                            </div>
                          </div>
                          {/* REQ-8 rev2: stacked Remarks / Attachments / Findings areas. */}
                          <InlineQuestionMeta
                            evidence={rowEvidence}
                            findings={rowFindings}
                            readOnly={readOnly}
                            onEvidenceRemoved={reloadEvidence}
                          />
                        </td>
                        <td className="table-list-cell">
                          <FormControl.Textarea
                            label=""
                            className="qresp-answer"
                            data-testid={`INP_questionnaire_response_${r.key}_answer`}
                            value={r.answer_text}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                              updateRow(idx, e.target.value, r.key)
                            }
                            onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
                              handleObsBlur(r.key, e.target.value)
                            }
                            rows={2}
                            noMargin
                            placeholder="Enter your answer…"
                            disabled={readOnly}
                          />
                          {obsErrors[r.key] && (
                            <div className="response-inline-warning">{obsErrors[r.key]}</div>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && !loadError && (
                  <tr>
                    <td className="table-list-cell modal-empty-text" colSpan={3}>
                      Loading…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Plan 05b-05 / STAFF-06 — single ObservationModal + FindingDetailModal */}
      <ObservationModal
        isOpen={modalRow !== null}
        onClose={() => setModalRow(null)}
        procedureInstanceId={instance.id}
        entityId={entityId}
        currentUserId={currentUserId}
        rowContext={modalRow?.context ?? { procedureName: instance.audit_procedure_name }}
        itemTypeId={ITEM_TYPE_QUESTIONNAIRE_RESPONSE}
        itemRecordId={modalRow?.itemRecordId ?? null}
        onCreated={(f) => {
          setFindings((prev) => [...prev, f]);
          reloadFindings();
        }}
      />
    </ResponseShell>
  );
};

export default QuestionnaireResponse;
